import { useForm } from '@tanstack/react-form';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { DATE_FORMATS } from '../../utils/date';
import { DateTimePicker } from '../atoms/DateTimePicker';
import { Button } from '../ui/button';
import { Field, FieldGroup, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Spinner } from '../ui/spinner';
import { Textarea } from '../ui/textarea';
import { ENCOUNTER_CLASSES } from './encounterClasses';

export interface PractitionerOption {
  id: string;
  name: string;
}

const formSchema = z.object({
  reasonText: z.string().trim().min(1),
  classCode: z.string().min(1),
  practitionerId: z.string().min(1),
  start: z.string().min(1),
  note: z.string().trim().min(1),
});

export type AddEncounterFormValues = z.infer<typeof formSchema>;

export interface AddEncounterFormProps {
  onCreate: (values: AddEncounterFormValues) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
  practitioners: PractitionerOption[];
  defaultPractitionerId: string;
}

export function AddEncounterForm({
  onCreate,
  onClose,
  isCreating,
  practitioners,
  defaultPractitionerId,
}: AddEncounterFormProps): React.ReactNode {
  const { t } = useTranslation('records');

  const form = useForm({
    defaultValues: {
      reasonText: '',
      classCode: 'AMB',
      practitionerId: defaultPractitionerId,
      start: format(new Date(), DATE_FORMATS.ISO_DATETIME_LOCAL),
      note: '',
    },
    validators: { onChange: formSchema, onMount: formSchema, onSubmit: formSchema },
    onSubmit: async ({ value }) => {
      await onCreate(formSchema.parse(value));
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-3"
    >
      <FieldGroup>
        <form.Field name="reasonText">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('reasonForVisit')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('reasonForVisitPlaceholder')}
                aria-label={t('reasonForVisit')}
                aria-required
                required
                data-testid="encounter-reason-input"
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="classCode">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('visitClass')}</FieldLabel>
              <Select value={field.state.value} onValueChange={field.handleChange}>
                <SelectTrigger
                  id={field.name}
                  aria-label={t('visitClass')}
                  className="w-full"
                  data-testid="encounter-class-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENCOUNTER_CLASSES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </form.Field>

        <form.Field name="practitionerId">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('provider')}</FieldLabel>
              <Select value={field.state.value} onValueChange={field.handleChange}>
                <SelectTrigger
                  id={field.name}
                  aria-label={t('provider')}
                  className="w-full"
                  data-testid="encounter-provider-select"
                >
                  <SelectValue placeholder={t('providerPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {practitioners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </form.Field>

        <form.Field name="start">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('start')}</FieldLabel>
              <DateTimePicker
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(value) => field.handleChange(value)}
                aria-label={t('start')}
                required
                data-testid="encounter-start-input"
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="note">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('encounterNote')}</FieldLabel>
              <Textarea
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('encounterNotePlaceholder')}
                aria-label={t('encounterNote')}
                aria-required
                required
                rows={6}
                data-testid="encounter-note-input"
              />
            </Field>
          )}
        </form.Field>
      </FieldGroup>

      <div className="flex gap-2">
        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || isSubmitting || isCreating}
              className="flex-1"
              aria-label={t('saveNewEncounter')}
              data-testid="encounter-submit-button"
            >
              {isSubmitting || isCreating ? <Spinner className="size-4" /> : t('add')}
            </Button>
          )}
        </form.Subscribe>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="flex-1"
          aria-label={t('cancelAddEncounter')}
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
}
